import { createFileRoute, Link, useSearch } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'

import { resetPassword, useBranding } from '@utils/api'
import { isValidUsername, normalizeUsername } from '@/validators/username'

type ResetPasswordSearch = {
  locale?: string
}

export const Route = createFileRoute('/secure/resetPassword')({
  component: ResetPassword,
  validateSearch: (search: Record<string, unknown>): ResetPasswordSearch => {
    return {
      locale: search.locale as string | undefined,
    }
  },
})

function ResetPassword() {
  const { locale } = useSearch({ from: '/secure/resetPassword' })
  const form = useForm({
    defaultValues: {
      username: '',
    },
  })
  const { branding, isLoading } = useBranding()
  const [success, setSuccess] = useState(false)
  const { t, i18n } = useTranslation()

  useEffect(() => {
    if (locale) i18n.changeLanguage(locale)
  }, [i18n, locale])

  const onSubmit = async (data: { username: string }) => {
    try {
      await resetPassword({ username: normalizeUsername(data.username) })
      setSuccess(true)
    } catch (error) {
      console.error('Reset password failed:', error)
    }
  }

  // Reset password form is short enough to be vertically centered.
  // Use h-[100svh] with overflow-hidden to prevent any scroll.
  return (
    <div className="h-[100svh] flex items-center justify-center p-4 overflow-hidden">
      <div className="w-full max-w-sm bg-white p-8 rounded-2xl space-y-6">
        {branding?.logo && (
          <div className="flex justify-center">
            <img
              src={branding.logo}
              alt={`${branding?.name}'s Logo`}
              className="object-contain h-20 max-w-[200px]"
            />
          </div>
        )}

        <div className="text-center">
          <h1 className="text-lg font-semibold my-2">
            {success
              ? t('secure.resetPassword.resetRequestedTitle')
              : t('secure.resetPassword.title')}
          </h1>
        </div>

        {!success ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground text-center">
              {t('secure.resetPassword.instructions')}
            </p>

            <Separator className="my-2" />

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="username"
                  rules={{
                    required: t('secure.login.errors.usernameRequired'),
                    validate: (value) =>
                      isValidUsername(value) ||
                      t('secure.login.errors.invalidUsername'),
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('secure.login.fieldNames.username')}</FormLabel>
                      <FormControl>
                        <Input type="text" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  className="w-full mt-2"
                  style={{
                    backgroundColor: branding?.buttonColor,
                    color: branding?.fontColor,
                  }}
                  disabled={form.formState.isSubmitting || isLoading}
                >
                  {form.formState.isSubmitting || isLoading ? (
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                      <span>{t('common.sending')}</span>
                    </div>
                  ) : (
                    t('secure.resetPassword.buttons.requestReset')
                  )}
                </Button>

                <div className="text-center">
                  <Link
                    to="/secure/login"
                    className="text-sm text-primary hover:underline"
                  >
                    {t('secure.resetPassword.buttons.cancel')}
                  </Link>
                </div>
              </form>
            </Form>
          </div>
        ) : (
          <div className="text-center space-y-4">
            <p className="text-sm text-muted-foreground">
              {t('secure.resetPassword.requestSuccessMessage')}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
